'use client';
import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  CheckCircleIcon, 
  ClipboardDocumentCheckIcon, 
  XMarkIcon, 
  InformationCircleIcon 
} from '@heroicons/react/24/outline';
import { Button } from '@/components/button';
import toast from 'react-hot-toast';

export default function InviteSuccessModal({ open, onClose, url }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                
                {/* Close Button */}
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-zinc-900 text-zinc-400 hover:text-zinc-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-zinc-900 dark:text-white">
                      Invite Link Created
                    </Dialog.Title>
                    
                    <div className="mt-2">
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        Share this secure link with your manager or coach.
                      </p>
                    </div>

                    {/* Copy Box */}
                    <div className="mt-4 flex rounded-md shadow-sm">
                      <div className="relative flex-grow focus-within:z-10">
                        <input
                          type="text"
                          readOnly
                          value={url}
                          className="block w-full rounded-none rounded-l-md border-0 py-2.5 pl-3 pr-3 text-zinc-900 ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 dark:bg-zinc-800 dark:text-white dark:ring-zinc-700"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 dark:bg-zinc-800 dark:text-white dark:ring-zinc-700 dark:hover:bg-zinc-700"
                      >
                        <ClipboardDocumentCheckIcon className="-ml-0.5 h-5 w-5 text-zinc-400" aria-hidden="true" />
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>

                    {/* IMPORTANT WARNING BLOCK */}
                    <div className="mt-5 rounded-xl bg-amber-50 p-4 border border-amber-100 dark:bg-amber-900/20 dark:border-amber-900/30">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <InformationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-bold text-amber-800 dark:text-amber-200">
                            Recipient Limit Warning
                          </h3>
                          <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                            <p className="mb-2">
                              If the person you are inviting <strong>already manages another team</strong>, they will be blocked from accepting this invite.
                            </p>
                            <p>
                              To manage multiple teams, they must upgrade to <strong>Pro</strong>.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="mt-6 sm:mt-6 sm:flex sm:flex-row-reverse">
                  <Button
                    color="blue"
                    className="w-full sm:ml-3 sm:w-auto"
                    onClick={onClose}
                  >
                    Done
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}